import type { LoaderFunctionArgs } from 'react-router-dom';
import { getAgreement } from '@/api/agreements';
import {
  type ApiErrorKind,
  classifyApiError,
  NO_ACCESS_MESSAGE,
} from '@/api/client';
import { getContractDetails, searchContracts } from '@/api/contracts';
import { GetAgreementParams } from '@/api/generated/endpoints/agreements/agreements.zod';
import { getContractsQueryQMin } from '@/api/generated/endpoints/contracts/contracts.zod';
import type {
  AgreementDetails,
  ContractDetails,
  ContractPage,
  ContractSummary,
  VinDetail,
} from '@/api/schemas';
import { getVinDetail } from '@/api/vehicle';
import { isValidVinInput, normalizeVinInput } from '@/lib/vin';

export const SEARCH_LIMIT = 200;
const VIN_CONTRACTS_LIMIT = 20;

export type Loaded<T> = { ok: true; value: T } | { ok: false; message: string };

async function attempt<T>(
  load: () => Promise<T>,
  formatError: (kind: ApiErrorKind) => string,
): Promise<Loaded<T>> {
  try {
    return { ok: true, value: await load() };
  } catch (error) {
    return { ok: false, message: formatError(classifyApiError(error)) };
  }
}

function searchErrorMessage(kind: ApiErrorKind): string {
  switch (kind) {
    case 'forbidden':
      return NO_ACCESS_MESSAGE;
    case 'timeout':
      return 'The search took too long. Try again.';
    case 'not-found':
    case 'unavailable':
      return 'Search failed. Try again.';
  }
}

function contractErrorMessage(kind: ApiErrorKind): string {
  switch (kind) {
    case 'forbidden':
      return NO_ACCESS_MESSAGE;
    case 'not-found':
      return 'Contract not found.';
    case 'timeout':
      return 'The contract took too long to load. Try again.';
    case 'unavailable':
      return 'Unable to load the contract. Try again.';
  }
}

function vehicleErrorMessage(kind: ApiErrorKind): string {
  switch (kind) {
    case 'forbidden':
      return NO_ACCESS_MESSAGE;
    case 'not-found':
      return 'Vehicle not found.';
    case 'timeout':
      return 'The vehicle details took too long to load. Try again.';
    case 'unavailable':
      return 'Unable to load vehicle details. Try again.';
  }
}

function vinContractsErrorMessage(kind: ApiErrorKind): string {
  switch (kind) {
    case 'forbidden':
      return NO_ACCESS_MESSAGE;
    case 'timeout':
      return 'The associated contracts took too long to load. Try again.';
    case 'not-found':
    case 'unavailable':
      return 'Unable to load associated contracts. Try again.';
  }
}

const AGREEMENT_NOT_FOUND = 'Agreement not found.';

function agreementErrorMessage(kind: ApiErrorKind): string {
  switch (kind) {
    case 'forbidden':
      return NO_ACCESS_MESSAGE;
    case 'not-found':
      return AGREEMENT_NOT_FOUND;
    case 'timeout':
      return 'The agreement took too long to load. Try again.';
    case 'unavailable':
      return 'Unable to load the agreement. Try again.';
  }
}

export type SearchLoaderData = {
  query: string;
  results: Loaded<ContractPage> | null;
};

export async function searchLoader({
  request,
}: LoaderFunctionArgs): Promise<SearchLoaderData> {
  const query = new URL(request.url).searchParams.get('q')?.trim() ?? '';

  if (query.length < getContractsQueryQMin) {
    return { query, results: null };
  }

  return {
    query,
    results: await attempt(
      () => searchContracts({ q: query, limit: SEARCH_LIMIT }),
      searchErrorMessage,
    ),
  };
}

export type ContractLoaderData = { contract: Promise<Loaded<ContractDetails>> };

export function contractLoader({
  params,
}: LoaderFunctionArgs): ContractLoaderData {
  return {
    contract: attempt(
      () => getContractDetails(params.contractId ?? ''),
      contractErrorMessage,
    ),
  };
}

export type VinLoaded = {
  detail: Loaded<VinDetail>;
  contracts: Loaded<ContractSummary[]>;
};

export type VinLoaderData =
  | { kind: 'invalid'; vin: string; message: string }
  | { kind: 'loaded'; vin: string; result: Promise<VinLoaded> };

export function vinLoader({ params }: LoaderFunctionArgs): VinLoaderData {
  const vin = normalizeVinInput(params.vin ?? '');

  if (!isValidVinInput(vin)) {
    return {
      kind: 'invalid',
      vin,
      message: 'Enter a valid VIN or chassis (8 to 17 characters).',
    };
  }

  const result = Promise.all([
    attempt(() => getVinDetail(vin), vehicleErrorMessage),
    attempt(
      async () =>
        (await searchContracts({ vin, limit: VIN_CONTRACTS_LIMIT })).contracts,
      vinContractsErrorMessage,
    ),
  ]).then(([detail, contracts]) => ({ detail, contracts }));

  return { kind: 'loaded', vin, result };
}

type AgreementLoaderData = {
  agreement: Promise<Loaded<AgreementDetails>>;
};

export function agreementLoader({
  params,
}: LoaderFunctionArgs): AgreementLoaderData {
  const parsed = GetAgreementParams.safeParse(params);
  if (!parsed.success) {
    return {
      agreement: Promise.resolve({ ok: false, message: AGREEMENT_NOT_FOUND }),
    };
  }

  return {
    agreement: attempt(
      () => getAgreement(parsed.data.agreementId),
      agreementErrorMessage,
    ),
  };
}
